import { Button as AntButton } from "antd";
import type { ButtonProps as AntButtonProps } from "antd";
import type { ButtonHTMLAttributes } from "react";
import { cn } from "@/shared/lib/utils";

type ButtonVariant = "default" | "outline" | "ghost" | "destructive" | "secondary" | "link";
type ButtonSize = "default" | "sm" | "lg" | "icon" | "icon-sm" | "icon-lg";

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "color">,
    Pick<AntButtonProps, "loading" | "htmlType"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  danger?: boolean;
}

// Maps the old custom variant system onto AntD's type/danger/ghost API.
// AntD only has 5 native "type"s (primary/default/dashed/link/text), so
// outline/secondary/ghost are all built on "default" + className overrides
// to preserve the exact old hover behavior (border+text flip to brand red).
const VARIANT_TO_ANTD: Record<ButtonVariant, { type: AntButtonProps["type"]; extraClass: string }> = {
  default: { type: "primary", extraClass: "" },
  destructive: { type: "primary", extraClass: "" }, // colorError === colorPrimary in theme, so identical render
  outline: { type: "default", extraClass: "msq-btn-outline" },
  secondary: { type: "default", extraClass: "msq-btn-outline" }, // visually identical to outline in the original
  ghost: { type: "text", extraClass: "msq-btn-ghost" },
  link: { type: "link", extraClass: "" },
};

const SIZE_TO_ANTD: Record<ButtonSize, AntButtonProps["size"]> = {
  default: "middle",
  sm: "small",
  lg: "large",
  icon: "middle",
  "icon-sm": "small",
  "icon-lg": "large",
};

const ICON_ONLY_SIZE_CLASS: Partial<Record<ButtonSize, string>> = {
  icon: "!h-8 !w-8 !rounded-full !p-0",
  "icon-sm": "!h-6 !w-6 !rounded-full !p-0",
  "icon-lg": "!h-10 !w-10 !rounded-full !p-0",
};

export function Button({
  variant = "default",
  size = "default",
  danger = false,
  className,
  children,
  ...props
}: ButtonProps) {
  const { type, extraClass } = VARIANT_TO_ANTD[variant];
  const isIconOnly = size.startsWith("icon");

  return (
    <AntButton
      type={type}
      danger={danger}
      size={SIZE_TO_ANTD[size]}
      shape={isIconOnly ? "circle" : "default"}
      className={cn("msq-stable-loading", extraClass, isIconOnly && ICON_ONLY_SIZE_CLASS[size], className)}
      {...props}
    >
      {children}
    </AntButton>
  );
}
